/**
 * News A/B comparison page — keyword-matched events (left) vs Claude-scored
 * sentiment articles (right), per symbol. Eyeball it for ~2 weeks; decide
 * whether the scored feed replaces, merges with, or sits alongside the
 * keyword feed in production surfaces.
 *
 * Steve directive 2026-05-23: path 1 picked after FMP's own sentiment
 * endpoints turned out to be legacy-locked. Scoring is via Claude CLI
 * with a trading-focused prompt — see scripts/research/score-stock-news.ts.
 */
'use client';

import { useState } from 'react';
import { useResource } from '@/lib/use-resource';
import { AppNav } from '@/components/AppNav';

interface KeywordRow {
  ts: string;
  headline: string;
  url?: string;
  matchedKeyword?: string;
  type?: string;
}

interface ScoredArticle {
  id: string;
  publishedDate: string;
  publisher: string | null;
  site: string | null;
  title: string;
  text: string;
  url: string;
  sentiment: number | null;
  sentimentLabel: string | null;
  materiality: 'low' | 'med' | 'high' | null;
  taxonomy: string[] | null;
  oneLineSummary?: string | null;
}

interface SymbolBundle {
  symbol: string;
  scope: ('held' | 'alpha-zone')[];
  keyword: KeywordRow[];
  scored: ScoredArticle[];
  aggregates: {
    keywordHits: number;
    scoredCount: number;
    scoredHigh: number;
    scoredMed: number;
    scoredLow: number;
    avgSentiment: number | null;
    minSentiment: number | null;
    maxSentiment: number | null;
  };
}

interface AbResponse {
  asOf: string;
  windowDays: number;
  symbolCount: number;
  bundles: SymbolBundle[];
}

function fmtDate(ts: string): string {
  const d = new Date(ts);
  if (!Number.isFinite(d.getTime())) return ts;
  const dow = d.toLocaleString('en-US', { weekday: 'short' });
  const mo = d.toLocaleString('en-US', { month: 'short' });
  const day = d.getDate();
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${dow} ${mo} ${day} · ${hh}:${mm}`;
}

function sentimentTone(s: number | null): string {
  if (s == null) return 'text-[#64748b]';
  if (s >= 0.3) return 'text-emerald-300';
  if (s <= -0.3) return 'text-red-300';
  return 'text-amber-300';
}

function materialityTone(m: 'low' | 'med' | 'high' | null): string {
  if (m === 'high') return 'bg-red-500/15 text-red-300 border-red-500/30';
  if (m === 'med') return 'bg-amber-500/10 text-amber-300 border-amber-500/30';
  return 'bg-[#1e293b] text-[#94a3b8] border-[#334155]';
}

export default function NewsAbPage() {
  const { data, refetch } = useResource<AbResponse>('/api/news-ab', 60_000);
  const [filter, setFilter] = useState<'all' | 'high' | 'held'>('all');
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0a0e14] text-white">
        <AppNav />
        <div className="max-w-6xl mx-auto px-6 py-10 text-[#64748b] text-sm">Loading...</div>
      </div>
    );
  }

  const bundles = data.bundles.filter(b => {
    if (filter === 'held') return b.scope.includes('held');
    if (filter === 'high') return b.aggregates.scoredHigh > 0;
    return true;
  });

  return (
    <div className="min-h-screen bg-[#0a0e14] text-[#cbd5e1]">
      <AppNav />
      <div className="max-w-7xl mx-auto px-6 py-6">
        <header className="mb-5 flex items-baseline justify-between">
          <div>
            <h1 className="text-[#c9a84c] text-[18px] font-bold tracking-wide uppercase">News Calibration · A / B</h1>
            <p className="text-[11px] text-[#64748b] mt-1">
              Trailing {data.windowDays}d. Left column = existing keyword-matched feed (portfolio-events.jsonl).
              Right column = new Claude-scored sentiment + taxonomy + materiality. As of {fmtDate(data.asOf)}.
            </p>
          </div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest">
            {(['all', 'high', 'held'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`rounded-md px-2 py-1 border transition-colors ${
                  filter === f ? 'bg-[#1e293b] text-white border-[#334155]' : 'text-[#64748b] border-[#1e293b] hover:text-white hover:border-[#334155]'
                }`}
              >
                {f === 'all' ? 'All scope' : f === 'high' ? 'High mat.' : 'Held only'}
              </button>
            ))}
            <button
              onClick={() => refetch()}
              className="rounded-md px-2 py-1 border border-[#1e293b] text-[#64748b] hover:text-white hover:border-[#334155]"
              title="Re-fetch"
            >
              ↻
            </button>
          </div>
        </header>

        {/* Per-symbol summary strip */}
        <div className="mb-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
          {bundles.map(b => {
            const active = selectedSymbol === b.symbol;
            return (
              <button
                key={b.symbol}
                type="button"
                onClick={() => setSelectedSymbol(active ? null : b.symbol)}
                className={`text-left px-2 py-2 rounded-md border transition-colors ${
                  active ? 'bg-amber-400/10 border-amber-400/60' : 'bg-[#0d1117] border-[#1e293b] hover:border-[#334155]'
                }`}
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-mono font-bold text-white text-[12px]">{b.symbol}</span>
                  {b.scope.includes('held') && <span className="text-[8px] uppercase tracking-wider px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">H</span>}
                  {b.scope.includes('alpha-zone') && <span className="text-[8px] uppercase tracking-wider px-1 py-0.5 rounded bg-blue-500/10 text-blue-300 border border-blue-500/20">AZ</span>}
                </div>
                <div className="text-[10px] text-[#64748b] mt-1 font-mono">
                  kw <span className="text-[#94a3b8]">{b.aggregates.keywordHits}</span>
                  <span className="mx-1">·</span>
                  scored <span className="text-[#94a3b8]">{b.aggregates.scoredCount}</span>
                  {b.aggregates.scoredHigh > 0 && <span className="text-red-300"> · H{b.aggregates.scoredHigh}</span>}
                </div>
                {b.aggregates.avgSentiment != null && (
                  <div className={`text-[10px] mt-0.5 font-mono ${sentimentTone(b.aggregates.avgSentiment)}`}>
                    avg sent {b.aggregates.avgSentiment >= 0 ? '+' : ''}{b.aggregates.avgSentiment.toFixed(2)}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Side-by-side detail. Empty state prompts selection. */}
        {selectedSymbol ? (
          <SymbolDetail bundle={bundles.find(b => b.symbol === selectedSymbol)!} />
        ) : (
          <div className="text-[12px] text-[#64748b] italic px-1 py-12 text-center border border-dashed border-[#1e293b] rounded">
            Click a symbol above to compare its keyword-matched feed vs Claude-scored articles side by side.
          </div>
        )}
      </div>
    </div>
  );
}

function SymbolDetail({ bundle }: { bundle: SymbolBundle }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* LEFT — keyword */}
      <section className="border border-[#1e293b] rounded-md bg-[#0d1117]">
        <header className="px-4 py-2 border-b border-[#1e293b] flex items-baseline justify-between">
          <h2 className="text-[12px] uppercase tracking-widest font-semibold text-[#94a3b8]">Keyword-matched feed</h2>
          <span className="text-[10px] text-[#64748b]">{bundle.keyword.length} events</span>
        </header>
        <div className="divide-y divide-[#1e293b]">
          {bundle.keyword.length === 0 && (
            <div className="text-[11px] text-[#64748b] italic px-4 py-6">No keyword-matched events in scope.</div>
          )}
          {bundle.keyword.map((e, i) => (
            <a key={i} href={e.url} target="_blank" rel="noopener noreferrer" className="block px-4 py-2 hover:bg-[#0a0e14] transition-colors">
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <span className="text-[9px] text-[#64748b] font-mono">{fmtDate(e.ts)}</span>
                {e.matchedKeyword && (
                  <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20">
                    {e.matchedKeyword}
                  </span>
                )}
              </div>
              <div className="text-[12px] text-[#e2e8f0] leading-snug">{e.headline}</div>
              {e.type && <div className="text-[9px] text-[#475569] uppercase tracking-wider mt-1">{e.type}</div>}
            </a>
          ))}
        </div>
      </section>

      {/* RIGHT — Claude-scored */}
      <section className="border border-[#1e293b] rounded-md bg-[#0d1117]">
        <header className="px-4 py-2 border-b border-[#1e293b] flex items-baseline justify-between">
          <h2 className="text-[12px] uppercase tracking-widest font-semibold text-emerald-300/80">Claude-scored news</h2>
          <span className="text-[10px] text-[#64748b]">
            {bundle.scored.length} articles · H{bundle.aggregates.scoredHigh} M{bundle.aggregates.scoredMed} L{bundle.aggregates.scoredLow}
          </span>
        </header>
        <div className="divide-y divide-[#1e293b]">
          {bundle.scored.length === 0 && (
            <div className="text-[11px] text-[#64748b] italic px-4 py-6">No scored articles in scope yet — run score-stock-news.ts.</div>
          )}
          {bundle.scored.map(a => (
            <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer" className="block px-4 py-2 hover:bg-[#0a0e14] transition-colors">
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <span className="text-[9px] text-[#64748b] font-mono">{fmtDate(a.publishedDate)}</span>
                <div className="flex items-center gap-1.5">
                  {a.materiality && (
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${materialityTone(a.materiality)}`}>
                      {a.materiality}
                    </span>
                  )}
                  {a.sentiment != null && (
                    <span className={`text-[9px] font-mono ${sentimentTone(a.sentiment)}`}>
                      {a.sentiment >= 0 ? '+' : ''}{a.sentiment.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-[12px] text-[#e2e8f0] leading-snug">{a.title}</div>
              {a.oneLineSummary && (
                <div className="text-[10px] text-[#94a3b8] italic mt-1 leading-snug">{a.oneLineSummary}</div>
              )}
              <div className="flex items-baseline justify-between gap-2 mt-1">
                {a.publisher && <span className="text-[9px] text-[#64748b]">{a.publisher}</span>}
                {a.taxonomy && a.taxonomy.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {a.taxonomy.map(t => (
                      <span key={t} className="text-[9px] text-[#475569] uppercase tracking-wider">· {t}</span>
                    ))}
                  </div>
                )}
              </div>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
